<template>
  <section v-if="members.length > 0" id="team" class="py-16 bg-gascon-blue">
    <div class="container mx-auto px-4">
      <h2 class="section-title text-white">Nos Gars</h2>

      <!-- Team carousel -->
      <div class="relative max-w-5xl mx-auto">
        <!-- Navigation arrows -->
        <button
          v-if="members.length > 3 && canScrollLeft"
          @click="scrollLeft"
          class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-gascon-gold text-gascon-blue w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity hidden md:flex"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          v-if="members.length > 3 && canScrollRight"
          @click="scrollRight"
          class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-gascon-gold text-gascon-blue w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity hidden md:flex"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- Team members container -->
        <div
          ref="teamContainer"
          class="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-6 pb-4 px-8 md:px-0"
          @scroll="updateScrollState"
        >
          <div
            v-for="member in members"
            :key="member.id"
            class="flex-shrink-0 w-[calc(100%-2rem)] md:w-[calc(33.333%-1rem)] snap-center md:snap-start"
          >
            <div class="relative rounded-lg overflow-hidden shadow-lg h-72 md:h-80 lg:h-96">
              <!-- Photo -->
              <div class="absolute inset-0 bg-gascon-gray">
                <img
                  v-if="member.photo_url"
                  :src="member.photo_url"
                  :alt="member.first_name"
                  class="w-full h-full object-cover object-top"
                />
                <svg v-else class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 text-gascon-blue/30" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>

              <!-- Info overlay -->
              <div class="absolute inset-0 p-4 flex flex-col justify-end text-center bg-gascon-blue/50">
                <h3 class="text-lg font-bold text-white mb-1">
                  {{ member.first_name }}
                </h3>
                <p class="text-white/90 text-sm leading-relaxed">
                  {{ member.description }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Dots indicator (mobile) -->
        <div class="flex justify-center gap-2 mt-4 md:hidden">
          <button
            v-for="(_, index) in members"
            :key="index"
            @click="scrollToMember(index)"
            class="w-2 h-2 rounded-full transition-colors"
            :class="currentIndex === index ? 'bg-gascon-gold' : 'bg-white/50'"
          ></button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'TeamSection',
  props: {
    members: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentIndex: 0,
      canScrollLeft: false,
      canScrollRight: true
    }
  },
  methods: {
    scrollLeft() {
      const container = this.$refs.teamContainer
      if (container) {
        container.scrollBy({ left: -container.offsetWidth / 3, behavior: 'smooth' })
      }
    },
    scrollRight() {
      const container = this.$refs.teamContainer
      if (container) {
        container.scrollBy({ left: container.offsetWidth / 3, behavior: 'smooth' })
      }
    },
    scrollToMember(index) {
      const container = this.$refs.teamContainer
      if (container) {
        // On mobile, each card takes full width minus padding
        const cards = container.querySelectorAll(':scope > div')
        if (cards[index]) {
          cards[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
        }
      }
    },
    updateScrollState() {
      const container = this.$refs.teamContainer
      if (container) {
        this.canScrollLeft = container.scrollLeft > 0
        this.canScrollRight = container.scrollLeft < container.scrollWidth - container.offsetWidth - 10
        // Calculate current index based on card width
        const cards = container.querySelectorAll(':scope > div')
        if (cards.length > 0) {
          const cardWidth = cards[0].offsetWidth + 24 // 24px = gap-6
          this.currentIndex = Math.round(container.scrollLeft / cardWidth)
        }
      }
    }
  }
}
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
